// src/render.js
// ----------------------------------------------------------------------------
// Pure presentational layer. Reads ONLY the Morphism view-models produced by
// `data.js` and turns them into DOM. No business logic, no side-effects:
// main.js calls `renderApp(data)` once, then wires interactivity in ui.js.
//
// Conventions:
//  - Every reveal element carries `class="… scroll"` + `data-reveal="<anim>"`.
//    scroll-reveal.js turns that pair into Motion UI's reveal states.
//  - Remote images use `class="lazyload"` + `data-src` (lazySizes).
//  - Icons are static inline SVGs from ./icons.js (currentColor, no deps).
// ----------------------------------------------------------------------------
import { icon } from './icons.js';

const esc = (s) =>
  (s == null ? '' : String(s).replace(/[&<>"'`]/g, (c) => `&#${c.charCodeAt(0)};`));

// Turn a link icon name from the schema ('github' | 'live') into an icon key.
const linkIcon = (name) => (name === 'live' ? icon('externalLink') : icon(name));

// Produce `class="BASE scroll [scroll-priority]" data-reveal="ANIM"` for inline use.
function rv(base, anim = 'fade-in', priority = false) {
  const cls = [base, 'scroll', priority && 'scroll-priority'].filter(Boolean).join(' ');
  return `class="${cls}" data-reveal="${anim}"`;
}

const initials = (name) =>
  name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

function contactLink(cl) {
  const isWeb = cl.icon === 'github' || cl.icon === 'linkedin';
  return `<a class="contact-link" href="${esc(cl.href)}" ${isWeb ? 'target="_blank" rel="noopener"' : ''} aria-label="${esc(cl.label)}">${icon(cl.icon)}</a>`;
}

function techTags(items, cls = 'tag tag--pill') {
  return items.map((t) => `<span class="${cls}">${esc(t)}</span>`).join('');
}

function cardLinks(links) {
  return links
    .map(
      (l) =>
        `<a class="card__link" href="${esc(l.href)}" target="_blank" rel="noopener" aria-label="${esc(l.label)}">${linkIcon(l.icon)} ${esc(l.label)}</a>`
    )
    .join('');
}

// ---------------------------------------------------------------------------
// Header + navigation
// ---------------------------------------------------------------------------
export function renderHeader(profile) {
  return `
<header class="site-header glass" id="header">
  <div class="container">
    <nav class="nav" role="navigation" aria-label="Main">
      <a href="#hero" class="nav__brand" style="display: flex; align-items: center; gap: 0.6rem;">
        <img src="${esc(profile.photo)}" href="/" alt="Logo" style="width: 28px; height: 28px; border-radius: 50%; object-fit: cover;">
        <!-- <span class="gradient-text" style="font-size: 1rem;">${esc(profile.name)}</span> -->
      </a>
      <button class="nav__toggle" id="navToggle" aria-label="Toggle menu" aria-controls="navList" aria-expanded="false">
        ${icon('menu')}
      </button>
      <ul class="nav__list" id="navList">
        <li><a href="#hero" class="nav__link">Home</a></li>
        <li><a href="#about" class="nav__link">About</a></li>
        <li><a href="#experience" class="nav__link">Experience</a></li>
        <li><a href="#work" class="nav__link">Work</a></li>
        <li><a href="#projects" class="nav__link">Projects</a></li>
        <li><a href="#academic" class="nav__link">Education</a></li>
        <li><a href="#awards" class="nav__link">Awards</a></li>
        <li><a href="#contact" class="nav__link">Contact</a></li>
      </ul>
      <button class="theme-toggle" id="themeToggle" aria-label="Toggle theme" aria-pressed="false">
        ${icon('moon')}
      </button>
    </nav>
  </div>
</header>`;
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
export function renderHero(profile) {
  const { name, title, heroDescription, bio, contactLinks } = profile;
  return `
<section id="hero" class="section hero">
  <canvas class="hero__canvas" id="heroCanvas" aria-hidden="true"></canvas>
  <div class="hero__content container">
    <img class="hero__photo" src="${esc(profile.photo)}" alt="${esc(name)}" width="180" height="180" loading="eager">
    <h1 class="hero__name" style="padding-top:1rem;" >Hi, I’m <span class="gradient-text">${esc(name)}</span></h1>
    <p class="hero__title" id="heroTitle" aria-live="polite">${esc(title)}</p>
    <p class="hero__tagline">${esc(heroDescription || bio)}</p>
    <div style="text-align: center;">
      <span ${rv('hero__badge', 'fade-in', true)}>Open to new opportunities</span>
    </div>
    <div class="hero__contact contact-list">
      ${contactLinks.map(contactLink).join('')}
    </div>
    <div class="hero__cta">
      <a class="btn btn--accent" href="./resume.html" target="_blank" rel="noopener">Download Resume</a>
      <a class="btn btn--ghost" href="#contact">Get in touch</a>
    </div>
  </div>
</section>`;
}

// ---------------------------------------------------------------------------
// About
// ---------------------------------------------------------------------------
export function renderAbout(profile, work, projects) {
  const { location, bio, skillGroups, contactLinks } = profile;
  const totalSkills = skillGroups.reduce((n, g) => n + g.items.length, 0);
  const stats = [
    { label: 'Based in', value: location },
    { label: 'Products shipped', value: `${work.length + projects.length}` },
    { label: 'Skills listed', value: `${totalSkills}` },
  ];
  return `
<section id="about" class="section">
  <div class="container">
    <header class="section__header">
      <h2 class="section__title">About</h2>
      <p class="section__subtitle">Craft engineer focused on fast, accessible, type-safe web systems.</p>
    </header>
    <div class="grid-2 gap-lg" style="align-items:start">
      <div ${rv('about__bio', 'fade-in')}>
        <p class="lead" style="text-align: justify;">${esc(bio)}</p>
        <div class="contact-list" style="margin-top:1.25rem">
          ${contactLinks.map(contactLink).join('')}
        </div>
      </div>
      <div class="grid gap-sm">
        ${stats.map((s, i) => `<div ${rv('stat-card', 'slide-in-up', i === 0)}>
          <div class="muted small">${esc(s.label)}</div>
          <div class="text-accent" style="font-weight:700">${esc(s.value)}</div>
        </div>`).join('')}
      </div>
    </div>
  </div>
</section>`;
}

// ---------------------------------------------------------------------------
// Skills
// ---------------------------------------------------------------------------
export function renderSkills(profile) {
  const { skillGroups } = profile;
  return `
<section id="skills" class="section">
  <div class="container">
    <header class="section__header">
      <h2 class="section__title">Skills</h2>
      <p class="section__subtitle">Technologies and tools I work with.</p>
    </header>
    <div class="skill-grid">
      ${skillGroups.map((g) => `<div ${rv('skill-group', 'slide-in-left')}>
        <div class="skill__label">${esc(g.label)}</div>
        <ul class="tags">${g.items.map((it) => `<li class="tag tag--pill">${esc(it)}</li>`).join('')}</ul>
      </div>`).join('')}
    </div>
  </div>
</section>`;
}

// ---------------------------------------------------------------------------
// Experience (timeline)
// ---------------------------------------------------------------------------
export function renderExperience(experience) {
  return `
<section id="experience" class="section">
  <div class="container">
    <header class="section__header">
      <h2 class="section__title">Experience</h2>
      <p class="section__subtitle">A selection of roles where I shipped products that reached real users.</p>
    </header>
    <ul class="timeline">
      ${experience.map((e, i) => `<li ${rv('timeline__item', i % 2 === 0 ? 'slide-in-left' : 'slide-in-right')}>
        <span class="timeline__dot" aria-hidden="true"></span>
        <div class="timeline__node">
          <div class="timeline__row">
            <h3 class="timeline__company text-accent">${esc(e.company)}</h3>
            <span class="timeline__period">${esc(e.period)} · ${esc(e.years)}</span>
          </div>
          <p class="timeline__role m-0">${esc(e.role)}</p>
          <p class="muted small">${esc(e.description)}</p>
          <div class="timeline__tags tags">${techTags(e.tags)}</div>
        </div>
      </li>`).join('')}
    </ul>
  </div>
</section>`;
}

// ---------------------------------------------------------------------------
// Work (case-study cards)
// ---------------------------------------------------------------------------
export function renderWork(work) {
  if (!work || !work.length) return '';
  return `
<section id="work" class="section">
  <div class="container">
    <header class="section__header">
      <h2 class="section__title">Case studies</h2>
      <p class="section__subtitle">Product work that I architected or led end-to-end.</p>
    </header>
    <div class="grid-2 gap-lg">
      ${work.map((w, i) => `<div ${rv('card work-card', i % 2 === 0 ? 'slide-in-left' : 'slide-in-right')}>
        <div class="card__media">
          <img class="card__img lazyload" data-src="${esc(w.thumbnail)}" alt="${esc(w.title)}">
        </div>
        <div class="card__body">
          <span class="card__role">${esc(w.role)}</span>
          <h3 class="card__title">${esc(w.title)}</h3>
          <p class="muted small">${esc(w.excerpt)}</p>
          <div class="tag-list">${techTags(w.technologies)}</div>
          <div class="card__links">${cardLinks(w.links)}</div>
        </div>
      </div>`).join('')}
    </div>
  </div>
</section>`;
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------
export function renderProjects(projects) {
  if (!projects || !projects.length) return '';
  return `
<section id="projects" class="section">
  <div class="container">
    <header class="section__header">
      <h2 class="section__title">Projects</h2>
      <p class="section__subtitle">Open-source tools and experiments I maintain.</p>
    </header>
    <div class="grid-3 gap-lg">
      ${projects.map((p, i) => `<div ${rv('card project-card', i % 3 === 0 ? 'slide-in-up' : i % 3 === 1 ? 'slide-in-left' : 'slide-in-right')}>
        <div class="card__media">
          <img class="card__img lazyload" data-src="${esc(p.thumbnail)}" alt="${esc(p.title)}">
        </div>
        <div class="card__body">
          <h3 class="card__title">${esc(p.title)}</h3>
          <p class="muted small">${esc(p.description)}</p>
          <div class="tags">${techTags(p.technologies)}</div>
          ${p.tags && p.tags.length ? `<div class="tag-list">${p.tags.map((t) => `<span class="tag tag--accent">${esc(t)}</span>`).join('')}</div>` : ''}
          <div class="card__links">${cardLinks(p.links)}</div>
        </div>
      </div>`).join('')}
    </div>
  </div>
</section>`;
}

// ---------------------------------------------------------------------------
// Academic
// ---------------------------------------------------------------------------
const typeName = (t) => (t === 'degree' ? 'Degree' : t);

export function renderAcademic(academic) {
  return `
<section id="academic" class="section">
  <div class="container">
    <header class="section__header">
      <h2 class="section__title">Education</h2>
      <p class="section__subtitle">Formal training and industry certifications.</p>
    </header>
    <ul class="academic-list">
      ${academic.map((a, i) => `<li ${rv('academic-item', i % 2 === 0 ? 'slide-in-left' : 'slide-in-right')}>
        <span class="academic__dot" aria-hidden="true"></span>
        <div class="academic__body">
          <span class="badge academic__type">${typeName(a.type)}</span>
          <h3 class="academic__title">${esc(a.title)}</h3>
          <p class="muted small">${esc(a.institution)} · ${esc(a.location)}</p>
          <p class="timeline__period">${esc(a.period)}</p>
          <p class="muted small">${esc(a.subtitle)}</p>
          <p class="muted small">${esc(a.body)}</p>
          ${a.url ? `<a class="card__link" href="${esc(a.url)}" target="_blank" rel="noopener" aria-label="Verify ${esc(a.title)} credential">${icon('externalLink')} Verify credential</a>` : ''}
        </div>
      </li>`).join('')}
    </ul>
  </div>
</section>`;
}

// ---------------------------------------------------------------------------
// Awards
// ---------------------------------------------------------------------------
export function renderAwards(awards) {
  if (!awards || !awards.length) return '';
  return `
<section id="awards" class="section">
  <div class="container">
    <header class="section__header">
      <h2 class="section__title">Awards & Certifications</h2>
      <p class="section__subtitle">Recognitions and achievements.</p>
    </header>
    <div class="grid-2 gap-lg">
      ${awards.map((a, i) => `<div ${rv('card award-card', i % 2 === 0 ? 'slide-in-left' : 'slide-in-right')}>
        <div class="card__body">
          <span class="card__role">${esc(a.date)}</span>
          <h3 class="card__title text-accent">${esc(a.title)}</h3>
          <p class="muted small mb-2">${esc(a.issuer)}</p>
          <p class="muted small">${esc(a.description)}</p>
          ${a.url ? `<div class="card__links" style="margin-top: 1rem;"><a class="card__link" href="${esc(a.url)}" target="_blank" rel="noopener" aria-label="${esc(a.title)}">${icon('externalLink')} View</a></div>` : ''}
        </div>
      </div>`).join('')}
    </div>
  </div>
</section>`;
}

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------
export function renderContact(profile) {
  return `
<section id="contact" class="section">
  <div class="container">
    <header class="section__header">
      <h2 class="section__title">Let’s build something</h2>
      <p class="section__subtitle">Have a role, project, or idea to explore? Drop a line.</p>
    </header>
    <div class="grid-2 gap-lg" style="align-items:start">
      <div ${rv('contact-details', 'fade-in')}>
        <div class="contact-list">${profile.contactLinks.map(contactLink).join('')}</div>
        <p class="muted" style="margin-top:1.5rem"><a href="tel:${(profile.phone || '').replace(/\\s/g, '')}" class="contact-info-link">${icon('phone')} ${esc(profile.phone)}</a></p>
        <p class="muted" style="margin-top:1rem"><a href="mailto:${esc(profile.email)}" class="contact-info-link">${icon('mail')} ${esc(profile.email)}</a></p>
      </div>
      <form class="contact-form form-grid" style="margin-top:1.5rem" id="contactForm" autocomplete="on" ${rv('form-wrapper', 'slide-in-right')}>
        <div class="form-field">
          <label for="cName">Name</label>
          <input class="form-control" type="text" id="cName" name="name" placeholder="Your name" required>
        </div>
        <div class="form-field">
          <label for="cEmail">Email</label>
          <input class="form-control" type="email" id="cEmail" name="email" placeholder="you@example.com" required>
        </div>
        <div class="form-field" style="grid-column:1/-1">
          <label for="cMessage">Message</label>
          <textarea class="form-control" id="cMessage" name="message" placeholder="What’s the project about?" rows="5" required></textarea>
        </div>
        <button class="btn btn--accent btn--wide" type="submit">Send message</button>
      </form>
    </div>
  </div>
</section>`;
}

// ---------------------------------------------------------------------------
// Footer + back-to-top
// ---------------------------------------------------------------------------
export function renderFooter(profile) {
  return `
<footer class="site-footer glass">
  <div class="container footer__grid">
    <div class="footer__brand">
      <div class="text-accent" style="font-weight:700">${esc(profile.name)}</div>
      <div class="muted small">${esc(profile.title)}</div>
    </div>
    <div class="footer__links contact-list">
      ${profile.contactLinks.map(contactLink).join('')}
      <a class="contact-link" href="./resume.html" target="_blank" rel="noopener" aria-label="Printable resume"><span class="visually-hidden">Resume</span>${icon('fileText')}</a>
    </div>
    <a href="./resume.html" target="_blank" rel="noopener" class="btn btn--ghost btn--small" id="printResume" aria-label="Print résumé">Print résumé ${icon('download')}</a>
  </div>
  <button class="back-to-top" id="topBtn" aria-label="Back to top">
    <span class="visually-hidden">Back to top</span>${icon('chevronUp')}
  </button>
</footer>`;
}

// ---------------------------------------------------------------------------
// Orchestrator: mount the whole page
// ---------------------------------------------------------------------------
export function renderApp(data) {
  const { profile, experience, work, projects, academic, awards } = data;
  const app = document.getElementById('app');
  if (!app) {
    console.error('render: #app root not found');
    return;
  }
  app.innerHTML = [
    renderHeader(profile),
    `<div class="tracing-beam-container"><div class="tracing-beam" id="tracingBeam"></div></div>`,
    `<div class="custom-cursor" id="customCursor"></div>`,
    `<main id="main">`,
    renderHero(profile),
    renderAbout(profile, work, projects),
    renderSkills(profile),
    renderExperience(experience),
    renderWork(work),
    renderProjects(projects),
    renderAcademic(academic),
    renderAwards(awards),
    renderContact(profile),
    `</main>`,
    renderFooter(profile),
  ].join('');
}
